import * as ToastPrimitive from '@radix-ui/react-toast';
import { AlertTriangle, CheckCircle2, Info, Loader2, X, XCircle } from 'lucide-react';
import { useSyncExternalStore, type ReactNode } from 'react';

import { cn } from '@/lib/cn';

/**
 * Toast — a dependency-free store plus a <Toaster> that renders it.
 *
 * Bottom-right stack, newest nearest the corner, at most four on screen (a fifth evicts
 * the oldest). Swipe right or press Esc while the toast has focus to dismiss; F8 focuses
 * the toast region. Motion collapses under `prefers-reduced-motion` via the tokens.
 *
 * The viewport sits at `--af-toast-offset` (default `--space-4`) above the bottom edge —
 * a screen with a pinned action rail raises that variable so toasts never cover it.
 */

/* ── model ────────────────────────────────────────────────────────────────── */

export type ToastVariant = 'default' | 'success' | 'error' | 'warning' | 'info' | 'loading';

export interface ToastActionSpec {
  label: string;
  onClick: () => void;
  /** Screen-reader alternative when the label alone is ambiguous. */
  altText?: string;
}

export interface ToastOptions {
  description?: ReactNode;
  action?: ToastActionSpec;
  /** ms; `Infinity` pins the toast. Defaults by variant. */
  duration?: number;
  /** Reuse an id to update a toast in place. */
  id?: string;
  dismissible?: boolean;
}

export interface ToastRecord {
  id: string;
  variant: ToastVariant;
  title: ReactNode;
  description?: ReactNode;
  action?: ToastActionSpec;
  duration: number;
  dismissible: boolean;
  /** false while the exit animation plays. */
  open: boolean;
}

const MAX_VISIBLE = 4;

const DEFAULT_DURATION: Record<ToastVariant, number> = {
  default: 5000,
  success: 4000,
  info: 5000,
  warning: 7000,
  error: 8000,
  loading: Number.POSITIVE_INFINITY,
};

/* ── store ────────────────────────────────────────────────────────────────── */

let toasts: ToastRecord[] = [];
let counter = 0;
const listeners = new Set<() => void>();
const removalTimers = new Map<string, ReturnType<typeof setTimeout>>();

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): ToastRecord[] {
  return toasts;
}

function cssMs(name: string, fallback: number): number {
  if (typeof window === 'undefined') return fallback;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (raw.endsWith('ms')) return Number.parseFloat(raw) || fallback;
  if (raw.endsWith('s')) return (Number.parseFloat(raw) || fallback / 1000) * 1000;
  return fallback;
}

function scheduleRemoval(id: string) {
  const existing = removalTimers.get(id);
  if (existing) clearTimeout(existing);
  removalTimers.set(
    id,
    setTimeout(
      () => {
        removalTimers.delete(id);
        toasts = toasts.filter((t) => t.id !== id);
        emit();
      },
      // Long enough for the exit animation to finish; --dur-normal is 1ms under
      // reduced motion, so the toast simply leaves immediately.
      cssMs('--dur-normal', 220) + 40,
    ),
  );
}

function upsert(record: ToastRecord) {
  const index = toasts.findIndex((t) => t.id === record.id);
  if (index >= 0) {
    const next = toasts.slice();
    next[index] = record;
    toasts = next;
  } else {
    toasts = [...toasts, record];
    const live = toasts.filter((t) => t.open);
    if (live.length > MAX_VISIBLE) {
      const evicted = live.slice(0, live.length - MAX_VISIBLE);
      toasts = toasts.map((t) => (evicted.includes(t) ? { ...t, open: false } : t));
      for (const t of evicted) scheduleRemoval(t.id);
    }
  }
  emit();
  return record.id;
}

function create(variant: ToastVariant, title: ReactNode, options: ToastOptions = {}): string {
  const id = options.id ?? `t${++counter}`;
  return upsert({
    id,
    variant,
    title,
    description: options.description,
    action: options.action,
    duration: options.duration ?? DEFAULT_DURATION[variant],
    dismissible: options.dismissible ?? true,
    open: true,
  });
}

/** Closes a toast (or all of them) with its exit animation. */
function dismiss(id?: string) {
  toasts = toasts.map((t) => (id === undefined || t.id === id ? { ...t, open: false } : t));
  for (const t of toasts) if (!t.open) scheduleRemoval(t.id);
  emit();
}

export interface ToastPromiseMessages<T> {
  loading: ReactNode;
  success: ReactNode | ((value: T) => ReactNode);
  error: ReactNode | ((error: unknown) => ReactNode);
  description?: ReactNode;
}

interface ToastApi {
  (title: ReactNode, options?: ToastOptions): string;
  success: (title: ReactNode, options?: ToastOptions) => string;
  error: (title: ReactNode, options?: ToastOptions) => string;
  warning: (title: ReactNode, options?: ToastOptions) => string;
  info: (title: ReactNode, options?: ToastOptions) => string;
  loading: (title: ReactNode, options?: ToastOptions) => string;
  dismiss: (id?: string) => void;
  promise: <T>(promise: Promise<T>, messages: ToastPromiseMessages<T>) => Promise<T>;
}

const toastFn = (title: ReactNode, options?: ToastOptions) => create('default', title, options);

export const toast: ToastApi = Object.assign(toastFn, {
  success: (title: ReactNode, options?: ToastOptions) => create('success', title, options),
  error: (title: ReactNode, options?: ToastOptions) => create('error', title, options),
  warning: (title: ReactNode, options?: ToastOptions) => create('warning', title, options),
  info: (title: ReactNode, options?: ToastOptions) => create('info', title, options),
  loading: (title: ReactNode, options?: ToastOptions) => create('loading', title, options),
  dismiss,
  promise: <T,>(promise: Promise<T>, messages: ToastPromiseMessages<T>): Promise<T> => {
    const id = create('loading', messages.loading, { description: messages.description });
    return promise.then(
      (value) => {
        create('success', resolveMessage(messages.success, value), { id });
        return value;
      },
      (error: unknown) => {
        create('error', resolveMessage(messages.error, error), { id });
        throw error;
      },
    );
  },
});

function resolveMessage<A>(message: ReactNode | ((arg: A) => ReactNode), arg: A): ReactNode {
  return typeof message === 'function' ? message(arg) : message;
}

/** Read-only view of the stack — for tests and for the shell's unread badge. */
export function useToasts(): ToastRecord[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/* ── presentation ─────────────────────────────────────────────────────────── */

const TOAST_CSS = `
@keyframes af-toast-in {
  from { opacity: 0; transform: translateX(calc(100% + var(--space-4))); }
  to { opacity: 1; transform: translateX(0); }
}
@keyframes af-toast-out {
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(calc(100% + var(--space-4))); }
}
@keyframes af-toast-swipe-out {
  from { transform: translateX(var(--radix-toast-swipe-end-x)); }
  to { transform: translateX(calc(100% + var(--space-4))); }
}
.af-toast[data-state='open'] { animation: af-toast-in var(--dur-normal) var(--ease-out); }
.af-toast[data-state='closed'] { animation: af-toast-out var(--dur-fast) var(--ease-in-out); }
.af-toast[data-swipe='move'] { transform: translateX(var(--radix-toast-swipe-move-x)); }
.af-toast[data-swipe='cancel'] { transform: translateX(0); transition: transform var(--dur-fast) var(--ease-out); }
.af-toast[data-swipe='end'] { animation: af-toast-swipe-out var(--dur-fast) var(--ease-out); }
/* Continuous-loop rate; the motion tokens describe transitions, not spinners. */
.af-toast-spin { animation: af-spin 1s linear infinite; }
`;

const VARIANT_ICON = {
  default: Info,
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
  loading: Loader2,
} as const;

const VARIANT_ICON_CLASS: Record<ToastVariant, string> = {
  default: 'text-muted',
  success: 'text-[var(--success)]',
  error: 'text-[var(--danger)]',
  warning: 'text-[var(--warning)]',
  info: 'text-[var(--info)]',
  loading: 'text-[var(--info)] af-toast-spin',
};

export interface ToasterProps {
  /** Distance from the bottom edge. Defaults to `var(--af-toast-offset, var(--space-4))`. */
  offsetBottom?: number | string;
  label?: string;
}

export function Toaster({ offsetBottom, label = 'Notifications ({hotkey})' }: ToasterProps) {
  const items = useToasts();

  return (
    <ToastPrimitive.Provider swipeDirection="right" duration={DEFAULT_DURATION.default}>
      <style href="af-toast" precedence="component">
        {TOAST_CSS}
      </style>

      {items.map((item) => (
        <ToastRow key={item.id} record={item} />
      ))}

      <ToastPrimitive.Viewport
        label={label}
        style={{ bottom: offsetBottom ?? 'var(--af-toast-offset, var(--space-4))' }}
        className={cn(
          'fixed right-4 z-[var(--z-toast)] m-0 flex list-none flex-col gap-2 p-0 outline-none',
          'w-[min(400px,calc(100vw-var(--space-8)))]',
        )}
      />
    </ToastPrimitive.Provider>
  );
}

function ToastRow({ record }: { record: ToastRecord }) {
  const Icon = VARIANT_ICON[record.variant];

  return (
    <ToastPrimitive.Root
      open={record.open}
      duration={record.duration}
      onOpenChange={(open) => {
        if (!open) dismiss(record.id);
      }}
      className={cn(
        'af-toast group pointer-events-auto flex items-start gap-3',
        'rounded-[var(--radius-lg)] border border-line bg-[var(--surface-overlay)] p-3',
        'shadow-[var(--shadow-3)]',
      )}
    >
      <Icon className={cn('mt-px size-4 shrink-0', VARIANT_ICON_CLASS[record.variant])} aria-hidden />

      <div className="min-w-0 flex-1">
        <ToastPrimitive.Title className="text-base font-medium leading-snug text-primary">
          {record.title}
        </ToastPrimitive.Title>
        {record.description ? (
          <ToastPrimitive.Description className="mt-1 text-sm leading-snug text-muted">
            {record.description}
          </ToastPrimitive.Description>
        ) : null}
      </div>

      {record.action ? (
        <ToastPrimitive.Action
          altText={record.action.altText ?? record.action.label}
          onClick={record.action.onClick}
          className={cn(
            'mt-px shrink-0 rounded-[var(--radius-sm)] border border-line px-2',
            'h-[var(--h-control-sm)] text-sm font-medium text-primary',
            'transition-colors ease-[var(--ease-out)] duration-[var(--dur-fast)]',
            'hover:border-line-strong hover:bg-[var(--surface-hover)]',
            'active:bg-[var(--surface-active)]',
            'focus-visible:outline-2 focus-visible:outline-offset-1 outline-[color:var(--focus)]',
          )}
        >
          {record.action.label}
        </ToastPrimitive.Action>
      ) : null}

      {record.dismissible ? (
        <ToastPrimitive.Close
          aria-label="Dismiss notification"
          className={cn(
            'mt-px flex size-[var(--h-control-sm)] shrink-0 items-center justify-center',
            'rounded-[var(--radius-sm)] text-muted transition-colors ease-[var(--ease-out)] duration-[var(--dur-fast)]',
            'hover:bg-[var(--surface-hover)] hover:text-primary',
            'focus-visible:outline-2 focus-visible:outline-offset-1 outline-[color:var(--focus)]',
          )}
        >
          <X className="size-3.5" aria-hidden />
        </ToastPrimitive.Close>
      ) : null}
    </ToastPrimitive.Root>
  );
}
